import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { Observable } from '@mimir/common/util/observable';
import { calculatedObservable, ObservableState } from '@mimir/common/util/observable';
import type { MediaSelection } from '@mimir/common/util/timeRange';
import type { FrameTime } from '@mimir/client-common/time/frameTime';
import type { Fraction } from '@mimir/common/util/fractionType';
import { clampFraction, divideFractions, frac, invertFraction, minFractions, multiplyFractionBy, roundFraction, subtractFractions } from '@mimir/common/util/integerMath';
import type { AudioVisualizationState, BufferedRange, ItemMediaPlayerOps, AudioInitializationCallbacks, PlayerError, PostLoadOperation } from '../itemMediaPlayerOps';
import type { AudioTrackConfig, AudioTrackConfigUpdate, PeakRange } from '../audioTrackConfig';
import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import { WorkletAudioHandler } from './audioWorkletAudioHandler';
import type { Logger } from '@mimir/common/types/logger';
import { addFuzzy, fractionToFuzzy, integerToFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import type { BufferCache } from '@mimir/common/util/bufferCache';
import type { Cleanable } from '@mimir/common/util/cleanable';
import { exceptionToString } from '@mimir/common/util/exception';
import type { SegmentProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import { type MediaPlayer, createCutterMediaPlayer } from '@mimir/browser/cutter/mediaPlayer/cutterMediaPlayer';
import workerProcessorUrl from '@mimir/browser/cutter/mediaPlayer/cutterMediaPlayerProcessor?worker&url';
import { abortableSleep } from '@mimir/common/async/abortableSleep';
import type { BufferRange } from '@mimir/cutter/frameProvider/frameProvider';
import { isAbortError } from '@mimir/common/util/abortControl';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import { StandalonePeaksManager } from '@mimir/web/model/standalonePeaksManager';
import type { PlaybackQuality, QualityChangeState, QualityOption, QualitySelectionResult } from '../playbackQuality';
import { VttHandler } from '@mimir/browser/subtitles/vttHandler';
import type { SubtitleCue } from '@mimir/common/types/subtitleCue';
import type { VisualizationMode } from '@mimir/browser/cutter/audioVisualizer';
import type { AudioLevelsData } from '@mimir/cutter/player/cutterCorePlayer';
import { CUTTER_ITEM_MEDIA_PLAYER_LOADED, CUTTER_ITEM_MEDIA_PLAYER_NOT_LOADED } from '@mimir/common/playwright/ariaLabels';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';

export class CutterItemMediaPlayer implements ItemMediaPlayerOps {
  private readonly player : MediaPlayer;
  private readonly canvas : HTMLCanvasElement;
  private readonly audioHandler : AudioHandler;
  private readonly cleanables : Cleanable[] = [];
  private activeItemLoading : ActiveItemLoading | null = null;
  private virtualClipRange = new ObservableState<{ start: Fraction, end: Fraction } | null>(null);
  private mediaDuration = new ObservableState<Fraction>(frac(0, 1));

  private requestedTime = new ObservableState<RequestedTime | null>(null);
  private positionRequestCounter : number = 0;

  private peaksManager : StandalonePeaksManager;

  private sourceMediaDuration : Observable<FuzzyValue>;
  private clampedVirtualClipDuration : Observable<FuzzyValue>;

  private vttHandler : VttHandler;

  private audioVisualizationState = new ObservableState<AudioVisualizationState | null>(null);

  private audioLevelsData = new ObservableState<AudioLevelsData | null>(null);

  constructor(
    private services: {
      bufferCache: BufferCache,
      logger: Logger,
      createSegmentProviderForItem: (itemId: string) => SegmentProvider,
      fetch: BasicFetch,
      itemPollingOps: ItemPollingOps,
    },
    parameters: {
      prematureVideoFrameReuseBugWorkaround: boolean,
    }
  ) {
    const { prematureVideoFrameReuseBugWorkaround } = parameters;
    this.vttHandler = new VttHandler(services);
    this.peaksManager = new StandalonePeaksManager(services);
    this.canvas = window.document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.position = 'absolute';
    this.canvas.style.objectFit = 'contain';
    // We use the ariaLabel on the canvas to signal to playwright whether the
    // player successfully loaded or not
    this.canvas.ariaLabel = CUTTER_ITEM_MEDIA_PLAYER_NOT_LOADED;
    this.canvas.onclick = (e) => {
      e.preventDefault();
      this.togglePlayPause();
    };
    this.audioHandler = new WorkletAudioHandler();
    this.player = createCutterMediaPlayer({
      services,
      listener: {
        onPlay: () => {
          this.paused.value = false;
        },
        onPause: () => {
          this.paused.value = true;
          this.speedFactor.value = 0;
        },
        onPositionChanged: (position: Fraction) => {
          this.actualPlayerTime.value = position;
        },
        onDurationChanged: (duration: Fraction) => {
          this.updateDuration(duration);
        },
        onBuffered: (bufferedRanges: BufferRange[]) => {
          const offset = this.virtualClipRange.value?.start ?? frac(0, 1);
          this.buffered.value = bufferedRanges.map((r): BufferedRange => {
            return {
              kind: r.kind,
              start: roundFraction(multiplyFractionBy(subtractFractions(r.start, offset), 1000)),
              end: roundFraction(multiplyFractionBy(subtractFractions(r.end, offset), 1000)),
            };
          });
        },
        onMuteStatesChanged: (muteStates: boolean[]) => {
          this.audioTrackConfigs.value = muteStates.map((isMuted) => ({ isSolo: false, isMuted }));
        },
        onAudioVisualizationModeChanged: (mode: VisualizationMode | null, available: VisualizationMode[]) => {
          // If there is only one mode available (or zero) there is no point in
          // showing the selector button.
          if (available.length > 1) {
            this.audioVisualizationState.value = { current: mode, available };
          } else {
            this.audioVisualizationState.value = null;
          }
        },
        onAudioLevelsData: (data: AudioLevelsData) => {
          this.audioLevelsData.value = data;
        },
        onVirtualClipRangeChanged: (range: { start: Fraction, end: Fraction } | null) => {
          this.virtualClipRange.value = range;
        },
      },
      audioHandler: this.audioHandler,
      workerProcessorUrl,
      canvas: this.canvas,
      prematureVideoFrameReuseBugWorkaround,
    });
    this.sourceMediaPosition =
      calculatedObservable(
        (requestedTime: RequestedTime | null, actualPlayerTime: Fraction, virtualClipRange: { start: Fraction, end: Fraction } | null) => {
          if (requestedTime !== null) {
            return fractionToFuzzy(requestedTime.time);
          }
          // The worker clamps playback to the virtual clip range, but the clip
          // range message arrives before the clamped position update. Clamp here
          // so downstream observables never see a position outside [start, end].
          if (virtualClipRange !== null) {
            return fractionToFuzzy(clampFraction(actualPlayerTime, virtualClipRange.start, virtualClipRange.end));
          }
          return fractionToFuzzy(actualPlayerTime);
        },
        this.cleanables,
        this.requestedTime,
        this.actualPlayerTime,
        this.virtualClipRange,
      );

    this.virtualClipMediaPosition =
      calculatedObservable(
        (sourceMediaPositionOrNull: FuzzyValue | null, virtualClipRange: { start: Fraction, end: Fraction } | null) => {
          const sourceMediaPosition = sourceMediaPositionOrNull ?? integerToFuzzy(0);
          if (virtualClipRange !== null) {
            return subtractFuzzy(sourceMediaPosition, fractionToFuzzy(virtualClipRange.start));
          } else {
            return sourceMediaPosition;
          }
        },
        this.cleanables,
        this.sourceMediaPosition,
        this.virtualClipRange,
      );


    this.clampedVirtualClipDuration =
      calculatedObservable(
        (mediaDuration: Fraction, virtualClipRange: { start: Fraction, end: Fraction } | null) => {
          if (virtualClipRange !== null) {
            const end = minFractions(virtualClipRange.end, mediaDuration);
            return fractionToFuzzy(subtractFractions(end, virtualClipRange.start));
          } else {
            return fractionToFuzzy(mediaDuration);
          }
        },
        this.cleanables,
        this.mediaDuration,
        this.virtualClipRange,
      );

    this.sourceClipMediaRange =
      calculatedObservable(
        (virtualClipRange: { start: Fraction, end: Fraction } | null, mediaDuration: Fraction): MediaSelection => {
          if (virtualClipRange === null) {
            return {
              whole: true,
              bounds: {
                start: fractionToFuzzy(frac(0, 1)),
                end: fractionToFuzzy(mediaDuration),
              }
            };
          } else {
            return {
              whole: false,
              bounds: {
                start: fractionToFuzzy(virtualClipRange.start),
                end: fractionToFuzzy(virtualClipRange.end)
              }
            };
          }
        },
        this.cleanables,
        this.virtualClipRange,
        this.mediaDuration,
      );

    this.virtualClipStart =
      calculatedObservable(
        (virtualClipRange: { start: Fraction, end: Fraction } | null): FuzzyValue | null => {
          return fractionToFuzzy(virtualClipRange?.start ?? frac(0, 1));
        },
        this.cleanables,
        this.virtualClipRange,
      );

    this.sourceMediaDuration =
      calculatedObservable(
        (mediaDuration: Fraction) => {
          return fractionToFuzzy(mediaDuration);
        },
        this.cleanables,
        this.mediaDuration,
      );

    this.cleanables.push(this.actualPlayerTime.observe((time) => this.vttHandler.setTime(time)));

    this.currentFrameTime =
      calculatedObservable(
        (sourceMediaPosition: FuzzyValue | null, sampleDuration: Fraction | null): FrameTime | null => {
          if (sourceMediaPosition === null || sampleDuration === null) return null;
          if (sourceMediaPosition.precise !== undefined) {
            const frameIndexFraction = divideFractions(sourceMediaPosition.precise, sampleDuration);
            return {
              frameIndex: roundFraction(frameIndexFraction),
              frameDuration: fractionToFuzzy(sampleDuration),
              rounded: false,
            };
          }
          return {
            frameIndex: Math.round(sourceMediaPosition.approximate / (sampleDuration.numerator / sampleDuration.denominator)),
            frameDuration: fractionToFuzzy(sampleDuration),
            rounded: true,
          };
        },
        this.cleanables,
        this.sourceMediaPosition,
        this.sampleDuration,
      );
  }

  // implements CutterCorePlayerTarget

  private actualPlayerTime = new ObservableState<Fraction>(frac(0, 1));

  private updateDuration(duration: Fraction): void {
    this.mediaDuration.value = duration;
  }

  // implements ItemMediaPlayerOps

  loadAudio(callbacks: AudioInitializationCallbacks): void {
    if (this.currentItemId === null) {
      this.services.logger.warn('Failed to load audio: no item is loaded');
      return;
    }
    this.peaksManager.loadItem(this.currentItemId, callbacks);
    return;
  }

  getChannelsAsMatrix(): number[][] | null {
    return null;
  }

  play(_ms?: number): void {
    this.setSpeedFactor(1.0);
  }

  pause(): void {
    this.setSpeedFactor(0.0);
  }

  setVtt(vttUrl: string | null): void {
    this.vttHandler.loadFromUrl(vttUrl);
  }

  private setPlayerPosition(time: Fraction): void {
    const requestId = this.positionRequestCounter++;
    this.requestedTime.value = { requestId, time };
    this.player.setPosition(time).then(() => {
      if (this.requestedTime.value === null) {
        return;
      }
      if (this.requestedTime.value.requestId === requestId) {
        this.requestedTime.value = null;
      }
    }).catch((err) => {
      if (!isAbortError(err)) {
        this.services.logger.error(`Failed to set player position: ${exceptionToString(err)}`);
      }
    });
  }

  seekToClampedSourceMediaPosition(sourcePos: FuzzyValue): void {
    if (sourcePos.precise !== undefined) {
      this.setPlayerPosition(sourcePos.precise);
    } else {
      const position = {
        numerator: Math.floor(sourcePos.approximate * 1000.0),
        denominator: 1000,
      };
      this.setPlayerPosition(position);
    }
  }

  seekAndGrabImage(_sourcePos: FuzzyValue): Promise<string> {
    throw new Error('TODO');
  }

  addPlayerErrorCallback(_callback: PlayerError): void {

  }

  removePlayerErrorCallback(_callback: PlayerError): void {

  }

  private currentItemId : string | null = null;

  setItemId(
    itemId: string,
    _itemType: 'video' | 'audio',
    _flags: {
      useFlacDownmix: boolean,
      useNewBufferingLogic: boolean,
      disableWebWorkersForFlacDownmix: boolean,
    },
    _callbacks?: {
      noPlayableArtifactYet?: () => void,
      postLoadOperation?: PostLoadOperation,
    },
  ): void {
    if (this.currentItemId === itemId) {
      return;
    }
    this.currentItemId = itemId;
    this.setSpeedFactor(0.0);

    if (this.activeItemLoading !== null) {
      this.activeItemLoading.abortController.abort();
      this.activeItemLoading = null;
    }

    const abortController = new AbortController();
    const promise = this.setItemIdAsync(itemId, abortController.signal)
      .catch((error) => {
        console.warn(`Failed to load item: ${exceptionToString(error)}`);
      });

    this.activeItemLoading = { abortController, promise };
  }

  private async setItemIdAsync(itemId: string, abortSignal: AbortSignal) {
    this.clearPlayer();
    while (true) {
      try {
        const loadItemResult = await this.player.loadItem(itemId, false, false);

        // We use the ariaLabel on the canvas to signal to playwright whether the
        // player successfully loaded or not
        this.canvas.ariaLabel = CUTTER_ITEM_MEDIA_PLAYER_LOADED;
        this.hasMedia.value = true;
        this.updateDuration(loadItemResult.duration);
        this.sampleDuration.value = invertFraction(loadItemResult.frameRate);
        this.activeItemLoading = null;

        return;
      } catch(error) {
        const retryIntervalMs = 1000;
        console.warn(`Failed to load item ${itemId}: ${exceptionToString(error)}`);
        console.log(`Will retry in ${retryIntervalMs} ms`);
        await abortableSleep(retryIntervalMs, abortSignal);
      }
    }
  }

  clearPlayer(): void {
    // We use the ariaLabel on the canvas to signal to playwright whether the
    // player successfully loaded or not
    this.canvas.ariaLabel = CUTTER_ITEM_MEDIA_PLAYER_NOT_LOADED;
    this.player.clear();
    this.hasMedia.value = false;
    this.audioTrackConfigs.value = [];
    this.peaksManager.clear();
    this.virtualClipRange.value = null;
    this.actualPlayerTime.value = frac(0, 1);
  }

  clear(): void {
    this.currentItemId = null;
    this.clearPlayer();
  }

  togglePlayPause(): void {
    if (this.paused.value) {
      this.play();
    } else {
      this.pause();
    }
  }

  setVolume(volume: number): void {
    this.player.setVolume(volume);
  }

  setMuted(muted: boolean): void {
    this.player.setMuted(muted);
  }

  appendVideoElementToElement(parent: Element): void {
    parent.append(this.canvas);
  }

  updateAudioTrackConfig(update: AudioTrackConfigUpdate[]): void {
    for (const u of update) {
      if (u.type === 'mute') {
        this.player.setAudioTrackMuteState(u.trackIndex, u.value);
      }
    }
  }

  setPosterImage(_src: string): void {

  }

  setSpeedFactor(speedFactor: number): void {
    this.speedFactor.value = speedFactor;
    this.player.setPlaybackRate({ numerator: Math.round(speedFactor * 4.0), denominator: 4.0 });
  }

  toggleFullscreen(): void {
    const canvas = this.canvas as any;
    if (canvas?.webkitEnterFullScreen) {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        canvas.webkitEnterFullScreen();
      }
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void canvas.requestFullscreen();
    }
  }

  private virtualClipPositionToSourceMediaPosition(virtualClipMediaPosition: FuzzyValue): FuzzyValue {
    if (this.virtualClipRange.value === null) {
      return virtualClipMediaPosition;
    } else {
      return addFuzzy(virtualClipMediaPosition, fractionToFuzzy(this.virtualClipRange.value.start));
    }
  }

  seekToVirtualClipMediaPosition(virtualClipMediaPosition: FuzzyValue): void {
    this.seekToClampedSourceMediaPosition(this.virtualClipPositionToSourceMediaPosition(virtualClipMediaPosition));
  }

  goForward(_ms: number): void {

  }

  goBackward(_ms: number): void {

  }

  goForwardBySecond(): void {

  }

  goForwardByTenSeconds(): void {

  }

  goBackwardBySecond(): void {

  }

  goBackwardByTenSeconds(): void {

  }

  goFrameBackward(): void {
    this.player.stepOneFrameBackward();
  }

  goFrameForward(): void {
    this.player.stepOneFrameForward();
  }

  getPeakSeriesCount(trackIndex: number): number {
    return this.peaksManager.getPeakSeriesCount(trackIndex);
  }

  getPeaksForSourceMediaRange(
    trackIndex: number,
    width: number,
    channel: number,
    range: PeakRange
  ): number[] {
    return this.peaksManager.getPeaksForSourceMediaRange(trackIndex, width, channel, range);
  }

  private sourceMediaPosition : Observable<FuzzyValue | null>;
  getSourceMediaPositionObs(): Observable<FuzzyValue | null> {
    return this.sourceMediaPosition;
  }

  private sampleDuration = new ObservableState<Fraction | null>(null);
  getSampleDuration(): FuzzyValue | null {
    return this.sampleDuration.value ? fractionToFuzzy(this.sampleDuration.value) : null;
  }

  private virtualClipStart : Observable<FuzzyValue | null>;
  getVirtualClipStartObs(): Observable<FuzzyValue | null> {
    return this.virtualClipStart;
  }

  private sourceClipMediaRange : Observable<MediaSelection>;

  getVirtualClipSourceMediaRangeObs(): Observable<MediaSelection> {
    return this.sourceClipMediaRange;
  }


  private virtualClipMediaPosition : Observable<FuzzyValue>;
  private paused = new ObservableState<boolean>(true);
  private speedFactor = new ObservableState<number>(0.0);
  private visible = new ObservableState<boolean>(false);
  private hasMedia = new ObservableState<boolean>(false);
  private loading = new ObservableState<boolean>(false);
  private hasAudioChannels = new ObservableState<boolean>(false);
  private videoAspectRatio = new ObservableState<number | null>(null);
  private playerAspectRatio = new ObservableState<number | null>(null);
  private seeking = new ObservableState<boolean>(false);
  private currentFrameTime : Observable<FrameTime | null>;
  private lastError = new ObservableState<ErrorEvent | null>(null);
  private buffered = new ObservableState<BufferedRange[]>([]);
  private hasGrowingMedia = new ObservableState<boolean>(false);
  private audioTrackConfigs = new ObservableState<AudioTrackConfig[]>([]);

  getSourceMediaDurationObs(): Observable<FuzzyValue> {
    return this.sourceMediaDuration;
  }

  getVirtualClipDurationObs(): Observable<FuzzyValue> {
    return this.clampedVirtualClipDuration;
  }

  getVirtualClipMediaPositionObs() { return this.virtualClipMediaPosition; }
  getPausedObs() { return this.paused.getObservable(); }
  getSpeedFactorObs() { return this.speedFactor.getObservable(); }
  getVisibleObs() { return this.visible.getObservable(); }
  getHasMediaObs() { return this.hasMedia.getObservable(); }
  getLoadingObs() { return this.loading.getObservable(); }
  getHasAudioChannelsObs() { return this.hasAudioChannels.getObservable(); }
  getVideoAspectRatioObs() { return this.videoAspectRatio.getObservable(); }
  getPlayerAspectRatioObs() { return this.playerAspectRatio.getObservable(); }
  getSeekingObs() { return this.seeking.getObservable(); }
  getCurrentFrameTimeObs() { return this.currentFrameTime; }
  getLastErrorObs() { return this.lastError.getObservable(); }
  getBufferedObs() { return this.buffered.getObservable(); }
  getHasGrowingMediaObs() { return this.hasGrowingMedia.getObservable(); }
  getAudioTrackConfigsObs() { return this.audioTrackConfigs.getObservable(); }
  getPeakCountObs() { return this.peaksManager.getPeakCountObs(); }

  destroy(): void {
    this.audioHandler.close();
    this.cleanables.splice(0).forEach((cleanable) => cleanable.cleanup());
  }

  async changeQuality(
    selectedQuality: PlaybackQuality,
    _flags: {
      useNewBufferingLogic: boolean,
    }
  ): Promise<QualitySelectionResult> {
    return {
      success: false,
      quality: selectedQuality,
      error: 'Chaning quality is not currently supported',
    };
  }

  getAvailableQualities(): QualityOption[] {
    return [];
  }

  getCurrentQuality(): PlaybackQuality | null {
    return null;
  }

  private qualityChangeState = new ObservableState<QualityChangeState>({ inProgress: false });
  getQualityChangeStateObs(): Observable<QualityChangeState> {
    return this.qualityChangeState.getObservable();
  }

  getCurrentSubtitleCueObs(): Observable<SubtitleCue | null> | null {
    return this.vttHandler.getCurrentSubtitleCueObs();
  }

  public getAudioVisualizationStateObs(): Observable<AudioVisualizationState | null> {
    return this.audioVisualizationState.getObservable();
  }

  setAudioVisualization(mode: VisualizationMode | null): void {
    this.player.setAudioVisualizationMode(mode);
  }

  getAudioLevelsDataObs(): Observable<AudioLevelsData | null> {
    return this.audioLevelsData.getObservable();
  }
}

interface ActiveItemLoading {
  promise: Promise<void>,
  abortController: AbortController,
}

interface RequestedTime {
  time: Fraction,
  requestId: number,
}
